import gql from 'graphql-tag';

import { Payline } from '@aps-management/primapp-common';

/**
 * Les deux retours de widget Payline, en portant le VERDICT DE LIVRAISON.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI CE DOUBLON DU PAQUET COMMUN
 * ---------------------------------------------------------------------------
 *
 * `Payline.api.checkPayment` et `Payline.api.captureWithToken` sont parfaits, à
 * un champ près : leurs documents GraphQL ne sélectionnent pas `delivery`. Or un
 * champ non sélectionné n'est jamais rendu, quoi que déclare le schéma : lire
 * `result.delivery.done` sur ces réponses donnerait `undefined` en silence.
 *
 * Le document est donc déclaré ici, exactement comme `RECORD_WIDGET_PAYMENT` et
 * `CAPTURE_SUBSCRIBER_WITH_INTENT` le sont déjà dans les écrans de ce dépôt, et
 * pour la même raison : toucher `primapp-common` imposerait une republication npm
 * qui retomberait sur les cinq autres fronts sans qu'aucun l'ait demandé.
 *
 * Extrait dans un module plutôt que recopié dans les deux écrans : les quatre
 * copies précédentes perdaient l'enveloppe `handlePaylineError`, si bien qu'une
 * panne réseau affichait au golfeur le message brut d'Apollo au lieu du message
 * traduit. C'est le constat 2 de la relecture du 21/08.
 *
 * ---------------------------------------------------------------------------
 * CE QUE ÇA RÉPARE
 * ---------------------------------------------------------------------------
 *
 * Le lot du 20/08 a fait lire `delivery.done` au paiement en UN CLIC. Le retour
 * du widget, lui, ne pouvait rien savoir : `paylineCheckPayment` déclenchait bien
 * une livraison côté master (`runWidgetImmediateDelivery`) mais jetait le verdict,
 * et `paylineCaptureWithToken` n'en déclenchait aucune.
 *
 * Conséquence mesurée sur la production entre le 01/08 et le 20/08 : 23
 * encaissements d'inscription orphelins, TOUS par le retour du widget, AUCUN par
 * le paiement en un clic. C'est ce chemin-ci, et lui seul, qui restait ouvert.
 *
 * ---------------------------------------------------------------------------
 * FIDÉLITÉ AU COMPORTEMENT D'ORIGINE
 * ---------------------------------------------------------------------------
 *
 * La sélection de champs et le traitement des erreurs sont repris de
 * `primapp-common` à l'identique, `delivery` mis à part. Ne pas s'en écarter :
 * les appelants traitent déjà ces cas, `assertCaptured` en tête.
 *
 * En particulier, `checkPayment` NE LÈVE PAS sur un paiement refusé : Monext rend
 * un `transactionId` même sur un refus, et c'est `assertCaptured` qui tranche
 * chez l'appelant. Ne pas ajouter de garde ici, elle ferait doublon avec une
 * autre qui connaît, elle, le contexte de l'écran.
 */

/*
 * Verdict de livraison, demandé sur les deux documents.
 *
 * Ne rien lire d'autre que `done`. `status` et `scope` sont du diagnostic, leur
 * liste appartient au serveur et s'allongera. Un écran qui n'attend pas ce champ
 * le reçoit nul et se comporte comme avant.
 *
 * Pas d'accent grave dans ce bloc : il vit dans un littéral de gabarit
 * JavaScript, qu'un accent grave fermerait au milieu du document.
 */
const CHECK_PAYMENT_WITH_DELIVERY = gql`
  query PaylineCheckPaymentWithDelivery($token: String!) {
    paylineCheckPayment(token: $token) {
      transactionId
      authorizationNumber
      status
      code
      maskedCardNumber
      cardBrand
      cardCountry
      delivery {
        done
        status
        scope
      }
    }
  }
`;

const CAPTURE_WITH_TOKEN_WITH_DELIVERY = gql`
  mutation PaylineCaptureWithTokenWithDelivery(
    $amount: Int!
    $token: String!
    $uuid: String!
    $reference: String!
    $customer: PaylineCustomer
  ) {
    paylineCaptureWithToken(amount: $amount, token: $token, uuid: $uuid, reference: $reference, customer: $customer) {
      transactionId
      token
      authorizationNumber
      resultCode
      maskedCardNumber
      cardBrand
      cardCountry
      expiryDate
      walletId
      delivery {
        done
        status
        scope
      }
    }
  }
`;

/**
 * Signature identique à `Payline.api.checkPayment`.
 *
 * @param {object} client    Client Apollo. Sur ce depot c'est le PROXY
 *                           (`apolloClient`), qui doit donc declarer `delivery`
 *                           sur `PaylinePaymentStatus` et sur `Payline`, ET le
 *                           selectionner dans ses propres documents de relais.
 * @param {object} variables `{ token }`
 * @returns {Promise<object>} la réponse de master, `delivery` compris
 */
export const checkPaymentWithDelivery = (client, variables) =>
  client
    .query({ query: CHECK_PAYMENT_WITH_DELIVERY, fetchPolicy: 'no-cache', variables })
    .then(({ data }) => {
      const response = data ? data.paylineCheckPayment : undefined;
      if (!response) {
        throw new Error('Impossible de vérifier le paiement.');
      }
      return response;
    })
    .catch((error) => {
      throw Payline.errors.handlePaylineError(error);
    });

/**
 * Signature identique à `Payline.api.captureWithToken`.
 *
 * @param {object} client    Client Apollo. Sur ce depot c'est le PROXY
 *                           (`apolloClient`), qui doit donc declarer `delivery`
 *                           sur `PaylinePaymentStatus` et sur `Payline`, ET le
 *                           selectionner dans ses propres documents de relais.
 * @param {object} variables `{ amount, token, uuid, reference, customer }`
 * @returns {Promise<object>} la réponse de master, `delivery` compris
 */
export const captureWithTokenWithDelivery = (client, variables) =>
  client
    .mutate({ mutation: CAPTURE_WITH_TOKEN_WITH_DELIVERY, variables })
    .then(({ data }) => {
      const response = data ? data.paylineCaptureWithToken : undefined;
      if (!Payline.errors.isPaylineSuccess(response)) {
        throw Payline.errors.handlePaylineError(null, response);
      }
      return response;
    })
    .catch((error) => {
      throw Payline.errors.handlePaylineError(error);
    });

export { CHECK_PAYMENT_WITH_DELIVERY, CAPTURE_WITH_TOKEN_WITH_DELIVERY };
